<template>
  <div class="result-view-content">
    <!-- ローディング状態 -->
    <LoadingSpinner v-if="isLoadingHistory" text="データを読み込み中..." size="medium" variant="inline" />

    <!-- 通常表示 -->
    <template v-else>
      <!-- チャットメッセージ -->
      <ChatMessage
        :message="headerMessage"
        :cat-icon-size="'medium'"
        :writer-type="historyWriterType"
        class="result-view-content__message"
      />

      <!-- AI感想文表示エリア -->
      <div class="result-view-content__feedback">
        <div class="result-view-content__feedback-content">
          <div
            ref="feedbackHtmlRoot"
            class="result-view-content__feedback-text"
            :class="{ 'result-view-content__feedback-text--html': isHtmlFeedback }"
          >
            <div v-if="isHtmlFeedback" v-html="$sanitize(displayFeedbackText)"></div>
            <div v-else>{{ displayFeedbackText }}</div>
          </div>
        </div>
      </div>

      <!-- アクションボタン -->
      <div class="result-view-content__actions">
        <button
          v-if="canApplyThemeGenerate"
          class="result-view-content__apply-button"
          type="button"
          :disabled="isApplyingThemeGenerate"
          @click="handleApplyThemeGenerate"
        >
          {{ isApplyingThemeGenerate ? $t("themeGenerate.apply.applying") : $t("themeGenerate.apply.button") }}
        </button>
        <button class="result-view-content__back-button" @click="handleBackClick">一覧にもどる</button>
      </div>

      <!-- 注意書き -->
      <div class="result-view-content__notice">
        <p class="result-view-content__notice-text">※ メニュー「過去ログ」から再度閲覧できます</p>
        <template v-if="isMatchingThemeResult">
          <p class="result-view-content__notice-text">{{ $t("themeMatching.result.disclaimerRecruitment") }}</p>
        </template>
        <template v-if="isGenerateThemeResult">
          <p class="result-view-content__notice-text">{{ $t("themeGenerate.apply.notice") }}</p>
        </template>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { HistoryItem } from "@/store/modules/assistHistory";
import { logError, logInfo, logWarn } from "@/lib/utils/logger";
import { ASSIST_MESSAGES } from "@/constants/assistMessages";
import { AssistFunctionType, HTML_FEEDBACK_FUNCTION_TYPES } from "@/lib/models/assist";
import { scrollToThemeField } from "@/lib/utils/themeMatchingIssues";
import { ThemeMatchingAnalytics, ThemeGenerateAnalytics } from "@/lib/utils/analytics/theme";
import {
  bindAssistHtmlResultInteractions,
  enhanceAssistHtmlResult,
} from "@/lib/utils/assistHtmlResult";
import type { ThemeMatchingCtaClick, ThemeMatchingIssue } from "@/lib/utils/assistHtmlResult";
import {
  dispatchThemeGenerateApply,
  extractThemeGenerateFieldsFromRoot,
} from "@/lib/utils/themeGenerateApply";

export default Vue.extend({
  name: "ResultViewContent",
  components: {
    ChatMessage,
    LoadingSpinner,
  },
  props: {
    feedbackText: {
      type: String,
      default: `あなたの作品を読ませていただきました。とても素晴らしい物語でした！

特に印象的だったのは、主人公の心情の変化が丁寧に描かれている点です。読者として、主人公と一緒に成長していく感覚を味わうことができました。

また、世界観の設定も非常に魅力的で、読み進めるうちにその世界に引き込まれていきました。細部まで考え抜かれた設定が、物語の説得力を高めていると感じます。

今後の展開も楽しみにしています。ぜひ続きも読ませてください！`,
    },
  },
  data() {
    return {
      historyData: null as HistoryItem | null,
      isLoadingHistory: false,
      historyError: null as string | null,
      unbindThemeMatchingIssues: null as (() => void) | null,
      unbindThemeMatchingBoardClicks: null as (() => void) | null,
      themeMatchingAnalytics: null as ThemeMatchingAnalytics | null,
      themeGenerateAnalytics: null as ThemeGenerateAnalytics | null,
      hasTrackedResultView: false,
      isApplyingThemeGenerate: false,
    };
  },
  computed: {
    ...mapGetters("assistNavigationModule", ["selectedItemId"]),
    ...mapGetters("assistSelectManuscriptModule", ["selectedNovelId"]),

    currentNovelId(): string | null {
      return this.selectedNovelId || (this.$route?.params?.novelId as string | undefined) || null;
    },

    // 履歴表示モードかどうか
    isHistoryMode(): boolean {
      return !!this.selectedItemId;
    },

    // 🆕 履歴のwriterTypeを取得
    historyWriterType(): string | null {
      if (this.isHistoryMode && this.historyData && this.historyData.writerType) {
        return this.historyData.writerType;
      }
      return null;
    },

    // functionTypeに応じた猫の最初のメッセージ
    headerMessage(): string {
      if (this.isHistoryMode && this.historyData && this.historyData.functionType) {
        const type = this.historyData.functionType;
        return ASSIST_MESSAGES[type as keyof typeof ASSIST_MESSAGES]?.header || ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.header;
      }
      return ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.header;
    },

    isMatchingThemeResult(): boolean {
      return this.isHistoryMode && this.historyData?.functionType === AssistFunctionType.MATCHING_THEME;
    },

    isGenerateThemeResult(): boolean {
      return this.isHistoryMode && this.historyData?.functionType === AssistFunctionType.GENERATE_THEME;
    },

    canApplyThemeGenerate(): boolean {
      return (
        this.isGenerateThemeResult &&
        !!this.historyData?.completed &&
        !this.historyData?.isError &&
        this.isHtmlFeedback
      );
    },

    // 表示するフィードバックテキスト
    displayFeedbackText(): string {
      if (this.isHistoryMode && this.historyData) {
        const isThemeMatching = this.historyData.functionType === AssistFunctionType.MATCHING_THEME;
        if (this.historyData.isError) {
          if (isThemeMatching) {
            return this.$t("themeMatching.result.error") as string;
          }
          if (this.historyData.functionType === AssistFunctionType.GENERATE_THEME) {
            return this.$t("themeGenerate.result.error") as string;
          }
          return "申し訳ございません。この感想の生成中にエラーが発生いたしました。しばらく時間をおいて再度お試しください。";
        }
        if (!this.historyData.completed) {
          if (isThemeMatching) {
            return this.$t("themeMatching.result.processing") as string;
          }
          if (this.historyData.functionType === AssistFunctionType.GENERATE_THEME) {
            return this.$t("themeGenerate.result.processing") as string;
          }
          return "現在AI読者が感想を執筆中です。しばらくお待ちください...";
        }
        if (this.historyData.fullFeedback) {
          return this.historyData.fullFeedback;
        }
        if (isThemeMatching) {
          return this.$t("themeMatching.result.notFound") as string;
        }
        if (this.historyData.functionType === AssistFunctionType.GENERATE_THEME) {
          return this.$t("themeGenerate.result.notFound") as string;
        }
        return "感想データが見つかりませんでした。";
      }
      return this.feedbackText;
    },

    // HTML結果の機能種別かつfullFeedbackがHTMLの場合true（履歴から開いたときの v-html 分岐）
    isHtmlFeedback(): boolean {
      return (
        this.isHistoryMode &&
        !!this.historyData &&
        HTML_FEEDBACK_FUNCTION_TYPES.includes(this.historyData.functionType) &&
        !!this.historyData.fullFeedback &&
        /<\/?[a-z][\s\S]*>/i.test(this.historyData.fullFeedback)
      );
    },
  },
  async mounted() {
    // 履歴モードの場合は履歴データを取得
    if (this.isHistoryMode) {
      await this.loadHistoryData();
    }
    if (this.$gtm) {
      this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
      this.themeGenerateAnalytics = new ThemeGenerateAnalytics(this.$gtm);
    }
  },
  beforeDestroy() {
    this.clearThemeMatchingBindings();
  },
  watch: {
    // selectedItemIdが変更された場合に履歴データを再取得
    selectedItemId: {
      handler(newId: string | null) {
        if (newId) {
          this.hasTrackedResultView = false;
          this.loadHistoryData();
        } else {
          this.historyData = null;
          this.hasTrackedResultView = false;
          this.clearThemeMatchingBindings();
        }
      },
      immediate: true,
    },
    // 同一画面で再マッチした結果に差し替わったときも result_view / match_point を再送する
    displayFeedbackText() {
      this.hasTrackedResultView = false;
      this.$nextTick(() => {
        this.setupThemeMatchingBindings();
        this.trackThemeGenerateResultView();
      });
    },
    historyData() {
      this.$nextTick(() => {
        this.setupThemeMatchingBindings();
        this.trackThemeGenerateResultView();
      });
    },
    isLoadingHistory(loading: boolean) {
      if (!loading) {
        this.$nextTick(() => {
          this.setupThemeMatchingBindings();
          this.trackThemeGenerateResultView();
        });
      }
    },
  },
  methods: {
    ...mapActions("assistNavigationModule", ["showHistory"]),
    ...mapActions("assistHistory", ["getHistoryDetail"]),

    clearThemeMatchingBindings(): void {
      if (this.unbindThemeMatchingIssues) {
        this.unbindThemeMatchingIssues();
        this.unbindThemeMatchingIssues = null;
      }
      if (this.unbindThemeMatchingBoardClicks) {
        this.unbindThemeMatchingBoardClicks();
        this.unbindThemeMatchingBoardClicks = null;
      }
    },

    getOrCreateMatchingAnalytics(): ThemeMatchingAnalytics | null {
      if (!this.themeMatchingAnalytics && this.$gtm) {
        this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
      }
      return this.themeMatchingAnalytics;
    },

    setupThemeMatchingBindings(): void {
      this.clearThemeMatchingBindings();
      if (!this.isMatchingThemeResult || !this.isHtmlFeedback) return;

      const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
      if (!root) return;

      enhanceAssistHtmlResult(root);

      const analytics = this.getOrCreateMatchingAnalytics();
      if (analytics && !this.hasTrackedResultView) {
        analytics.trackResultView({ novelId: this.currentNovelId, root });
        this.hasTrackedResultView = true;
      }

      const unbind = bindAssistHtmlResultInteractions(root, {
        onIssueClick: (issue) => this.handleThemeMatchingIssueClick(issue),
        onBoardClick: (payload) => this.handleThemeMatchingBoardClick(payload),
      });
      this.unbindThemeMatchingIssues = unbind;
      this.unbindThemeMatchingBoardClicks = null;
    },

    handleThemeMatchingBoardClick(payload: ThemeMatchingCtaClick): void {
      this.getOrCreateMatchingAnalytics()?.trackCtaClick({
        cta: "board",
        novelId: this.currentNovelId,
        companyId: payload.companyId,
      });
    },

    handleThemeMatchingIssueClick(issue: ThemeMatchingIssue): void {
      // 改善指摘本文は画面上に表示済み。クリック時は該当フィールドへ誘導する
      let applied = false;
      if (issue.field) {
        applied = scrollToThemeField(issue.field);
      }

      const analytics = this.getOrCreateMatchingAnalytics();
      analytics?.trackImproveView({
        novelId: this.currentNovelId,
        companyId: issue.companyId,
        field: issue.field,
      });
      analytics?.trackImproveApply({
        novelId: this.currentNovelId,
        companyId: issue.companyId,
        field: issue.field,
        applied,
      });
    },

    getOrCreateGenerateAnalytics(): ThemeGenerateAnalytics | null {
      if (!this.themeGenerateAnalytics && this.$gtm) {
        this.themeGenerateAnalytics = new ThemeGenerateAnalytics(this.$gtm);
      }
      return this.themeGenerateAnalytics;
    },

    trackThemeGenerateResultView(): void {
      if (!this.isGenerateThemeResult || !this.isHtmlFeedback || this.hasTrackedResultView) return;
      const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
      const fields = extractThemeGenerateFieldsFromRoot(root);
      this.getOrCreateGenerateAnalytics()?.trackResultView({
        novelId: this.currentNovelId,
        fieldCount: Object.keys(fields).length,
      });
      this.hasTrackedResultView = true;
    },

    handleApplyThemeGenerate(): void {
      if (!this.canApplyThemeGenerate || this.isApplyingThemeGenerate) return;
      this.isApplyingThemeGenerate = true;
      try {
        const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
        const fields = extractThemeGenerateFieldsFromRoot(root);
        if (Object.keys(fields).length === 0) {
          Vue.notify({
            title: this.$t("themeGenerate.apply.empty") as string,
            type: "warn",
          });
          return;
        }
        dispatchThemeGenerateApply(fields);
      } finally {
        this.isApplyingThemeGenerate = false;
      }
    },

    async handleBackClick(): Promise<void> {
      try {
        // assist-navigationモジュールのshowHistoryアクションを呼び出して履歴画面に遷移
        await this.showHistory();

        // 従来のback-clickイベントも発火（親コンポーネントでの追加処理用）
        this.$emit("back-click");
      } catch (error) {
        logError({ module: "ResultViewContent", action: "handleBackClick" }, "履歴画面への遷移に失敗", error);
        // エラーが発生した場合でも従来のイベントは発火
        this.$emit("back-click");
      }
    },

    async loadHistoryData(): Promise<void> {
      if (!this.selectedItemId) {
        this.historyData = null;
        return;
      }

      try {
        this.isLoadingHistory = true;
        this.historyError = null;

        logInfo({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細を取得中", {
          selectedItemId: this.selectedItemId,
        });

        // assistHistoryモジュールから履歴詳細を取得
        const detail = await this.getHistoryDetail(this.selectedItemId);

        if (detail) {
          this.historyData = detail;
          logInfo({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細取得完了", {
            id: detail.id,
            completed: detail.completed,
          });
        } else {
          this.historyError = "履歴データが見つかりませんでした";
          logWarn({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細が見つかりません", {
            selectedItemId: this.selectedItemId,
          });
        }
      } catch (error: any) {
        logError({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細の取得に失敗", error);
        this.historyError = error.message || "履歴詳細の取得に失敗しました";
      } finally {
        this.isLoadingHistory = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@import "@/styles/assist-result-html.scss";
@import url("https://fonts.googleapis.com/css2?family=Yusei+Magic&display=swap");

.result-view-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  min-height: 0;
}

.result-view-content__message {
  margin-bottom: 4px;
  flex-shrink: 0;
}

.result-view-content__feedback {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.result-view-content__feedback-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.result-view-content__feedback-intro {
  margin: 0 0 12px 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  flex-shrink: 0;
}

.result-view-content__feedback-text--html {
  @include assist-html-result;
}

.result-view-content__feedback-text {
  font-size: var(--font-size-xs);
  line-height: 1.6;
  color: var(--color-text-normal);
  white-space: pre-line;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 12px 4px 0;

  /* スクロールバーのスタイリング */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: var(--radius-xs);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: var(--radius-xs);

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  /* HTML表示時はwhite-space: normalに */
  &--html {
    white-space: normal;
  }

  /* キャッチコピー表示用スタイル（v-htmlで挿入されるHTML用） */
  @include assist-catchcopy-result;
}

.result-view-content__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  flex-shrink: 0;
}

.result-view-content__apply-button {
  min-width: 200px;
  max-width: 240px;
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-rounded);
  padding: 16px 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.9);
  }

  &:disabled {
    background-color: var(--color-surface-base);
    color: var(--color-inactive);
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}

.result-view-content__back-button {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-rounded);
  padding: 16px 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(0.9);
  }

  &:active {
    filter: brightness(0.85);
    transform: translateY(1px);
  }

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}

.result-view-content__notice {
  text-align: center;
  padding-top: 8px;
  flex-shrink: 0;
}

.result-view-content__notice-text {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

/* レスポンシブ対応 */
@media (max-width: 576px) {
  .result-view-content {
    gap: 12px;
  }

  .result-view-content__feedback {
    padding: 12px;
  }

  .result-view-content__feedback-intro {
    font-size: var(--font-size-xs);
  }

  .result-view-content__feedback-text {
    font-size: var(--font-size-xs);
  }

  .result-view-content__back-button {
    padding: 14px 20px;
    font-size: var(--font-size-xs);
  }

  .result-view-content__notice-text {
    font-size: var(--font-size-xs);
  }

}
</style>
